import axios from 'axios'
import { canUseDOM } from 'exenv'
import { apiUrl, appVersionUrl } from '../../config/config'
import { listSize } from '../lib/items'
import buildTimestamp from '../../buildtimestamp.json'


class BadApiResponseError extends Error {
  constructor(message, response) {
    super(message)
    const dataSample = String(response?.data).replace(/\s+/g, ' ').replace(/> </g, '><').trim()
    this.data = dataSample.length < 140 ? dataSample : (dataSample.slice(0, 130) + '… (truncated)')
    this.name = 'BadApiResponseError'
    this.method = response?.config?.method
    this.url = response?.config?.url
    this.status = response?.status
    this.headers = response?.headers
    this.note = 'This error generally happens when the API call is intercepted by a captive portal or similar, ' +
                'and the response is not what we expect. These are very rare nowadays because of HTTPS, but do ' +
                'still happen in corporate managed devices that have been configured with SSL hijacking/inspection. ' +
                'There is nothing we can do to resolve such issues. We currently log this error to Sentry to keep ' +
                'track of how often it happens. We could maybe reload the page automatically so user can re-authenticate, ' +
                'but we don\'t know how the login flow works, so reloading the page might even be somehow worse. Also ' +
                'afraid of causing reload loops if we ever have bug in our code that causes this error to be thrown.'
  }
}

/**
 * Check for bad responses caused by captive portals. These are rare because of HTTPS, but can happen
 * in corporate managed devices that have been configured with SSL hijacking / inspection. Better to
 * throw here than let bad responses propagate and cause random errors all over the place.
 * @param {import('axios').AxiosResponse} response
 * @throws {BadApiResponseError}
 * @returns {{data: unknown, timestamp: number}} response.data
 */
function validateResponse(response) {
  const is200 = response?.status === 200
  const notJSON = !response?.headers['content-type']?.startsWith('application/json')
  const notTimestamped = !(response?.data?.timestamp > 0)
  if (is200 && (notJSON || notTimestamped)) {
    throw new BadApiResponseError('Unexpected response type from API', response)
  }
  return response.data
}



async function apiGet(path, params, jwt) {
  // Manually passing JTW for server-side rendering. SSR does not do any POST/etc requests, so we only
  // set it for GET requests. For client side requests JTW is always automatically passed via cookie.
  const headers = { Accept: 'application/json' }
  if (!canUseDOM && jwt) {
    headers['Authorization'] = `Bearer ${jwt}`
  }
  const response = await axios({
    method: 'GET',
    url: apiUrl + path,
    params: params ?? {},
    headers,
  })
  return validateResponse(response)
}

async function apiPost(path, data) {
  const headers = { Accept: 'application/json' }
  const response = await axios({
    method: 'POST',
    url: apiUrl + path,
    data: data ?? {},
    headers,
  })
  return validateResponse(response)
}

async function apiPut(path, data) {
  const headers = { Accept: 'application/json' }
  const response = await axios({
    method: 'PUT',
    url: apiUrl + path,
    data: data ?? {},
    headers,
  })
  return validateResponse(response)
}

async function apiDelete(path) {
  const headers = { Accept: 'application/json' }
  const response = await axios({
    method: 'DELETE',
    url: apiUrl + path,
    headers,
  })
  return validateResponse(response)
}

/**
 * Fetch the version string of the client currently deployed on server. During SSR we
 * automatically know the version, so we can return it directly without network request.
 * @returns {Promise<string>} Semver string of the client deployed on server
 */
export async function getAppVersion() {
  if (!canUseDOM) {
    return buildTimestamp
  }
  const response = await axios.get(appVersionUrl)
  return validateResponse(response).data.version
}



export function setKeyValue(key, value) {
  return apiPut(`/profile/keyvalue/${key}`, {value})
}


export function getLogo() {
  return apiGet('/sitelogo/')
}

export function getTickers(tickerSlug = null) {
  return apiGet(`/tickers/${tickerSlug ?? ''}`)
}

export function postClick(itemId) {
  return apiPost(`/items/${itemId}/click`)
}

export function postVote(itemId, voteType) {
  return apiPut(`/items/${itemId}/vote`, {vote: voteType})
}

export function postReport(itemId, reportType) {
  return apiPut(`/items/${itemId}/report`, {report: reportType})
}

export function getStatsForItems(item_ids) {
  return apiPost('/items/stats', {item_ids})
}

export function getItem(itemId) {
  return apiGet(`/items/${itemId}`)
}


function getItems(path, jwt, { timestamp, batch, limit = listSize, blacklisted_categories, blacklisted_tags, whitelisted_categories, whitelisted_tags, paywalled, category, tag } = {}) {
  const params = {
    limit: limit,
  }
  if (timestamp) {
    params.max_timestamp = timestamp + 1
  }
  if (batch) {
    params.batch = batch
  }
  if (blacklisted_categories) {
    params.blacklisted_categories = blacklisted_categories
  }
  if (blacklisted_tags) {
    params.blacklisted_tags = blacklisted_tags
  }
  if (whitelisted_categories) {
    params.whitelisted_categories = whitelisted_categories
  }
  if (whitelisted_tags) {
    params.whitelisted_tags = whitelisted_tags
  }
  if (paywalled) {
    params.paywalled = paywalled
  }
  if (category) {
    params.category = category
  }
  if (tag) {
    params.tag = tag
  }
  const userType = jwt ? 'user' : 'anon'
  return apiGet(`/itemlists/${userType}/${path}`, params, jwt)
}

export function getNewest(jwt, params) {
  return getItems('newest', jwt, params)
}

export function getUserItems(jwt, params) {
  return getItems('followed', jwt, params)
}

export function getFeatured(jwt, params) {
  return getItems('featured', jwt, params)
}

export function getPaywalled(jwt, params) {
  return getItems('paywalled', jwt, params)
}

export function getMostRead(jwt, params) {
  return getItems('top-read', jwt, params)
}

export function getMostVoted(jwt, params) {
  return getItems('top-voted', jwt, params)
}

export function getItemsForCategory(jwt, categorySlug, params) {
  return getItems(`category/${categorySlug}`, jwt, params)
}

export function getItemsForTag(jwt, tagSlug, params) {
  return getItems(`tag/${tagSlug}`, jwt, params)
}

export function getPopular(jwt, params) {
  return getItems('sidebar-popular', jwt, params)
}

export function getUserFeedItems(jwt, params) {
  return getItems('sidebar-user-feeds', jwt, params)
}

export function getTrending() {
  return apiGet('/trends')
}

export function getBreakingNews(jwt, params) {
  return getItems('breaking', jwt, params)
}

export function searchItems(term, page) {
  const params = {
    q: term,
    limit: listSize,
    page: page || '1',
  }
  return apiGet('/search/', params)
}

export function getCategories() {
  return apiGet('/categories')
}

export function getAllTags() {
  return apiGet('/tags/all')
}
export function getSources() {
  return apiGet('/sources')
}

export function sendAdminEmail(subject, replyto, postData) {
  postData._subject = subject
  postData._replyto = replyto
  return apiPost('/adminemail/', postData)
}

export function getReadyMadeProfiles() {
  return apiGet('/readymadeprofiles')
}

export function setUserVoteFilter(threshold) {
  return apiPut('/profile/vote-filter', { threshold })
}


export function addUserWordFilter(filter) {
  return apiPut(`/profile/word-filters/${encodeURIComponent(filter)}`, null)
}
export function removeUserWordFilter(filter) {
  return apiDelete(`/profile/word-filters/${encodeURIComponent(filter)}`)
}


export function addUserSavedSearch(searchTerm) {
  return apiPut(`/profile/searches/${encodeURIComponent(searchTerm)}`, null)
}
export function removeUserSavedSearch(searchTerm) {
  return apiDelete(`/profile/searches/${encodeURIComponent(searchTerm)}`)
}


export function addUserFeed(url) {
  return apiPost('/profile/feeds/', {url})
}
export function updateUserFeed(feedId, category_id) {
  return apiPut(`/profile/feeds/${feedId}`, {category_id})
}
export function removeUserFeed(feedId) {
  return apiDelete(`/profile/feeds/${feedId}`)
}


export function addWhitelistTag(tagId) {
  return apiPost(`/profile/tags/whitelisted/${tagId}`, null)
}
export function removeWhitelistTag(tagId) {
  return apiDelete(`/profile/tags/whitelisted/${tagId}`)
}
export function addBlacklistTag(tagId) {
  return apiPost(`/profile/tags/blacklisted/${tagId}`, null)
}
export function removeBlacklistTag(tagId) {
  return apiDelete(`/profile/tags/blacklisted/${tagId}`)
}


export function addBlacklistSource(sourceId) {
  return apiPost(`/profile/sources/blacklisted/${sourceId}`, null)
}
export function removeBlacklistSource(sourceId) {
  return apiDelete(`/profile/sources/blacklisted/${sourceId}`)
}


export function addSubscribeSource(sourceId) {
  return apiPost(`/profile/sources/subscriptions/${sourceId}`, null)
}
export function removeSubscribeSource(sourceId) {
  return apiDelete(`/profile/sources/subscriptions/${sourceId}`)
}


export function addWhitelistCategory(categoryId) {
  return apiPost(`/profile/categories/whitelisted/${categoryId}`, null)
}
export function removeWhitelistCategory(categoryId) {
  return apiDelete(`/profile/categories/whitelisted/${categoryId}`)
}
export function addBlacklistCategory(categoryId) {
  return apiPost(`/profile/categories/blacklisted/${categoryId}`, null)
}
export function removeBlacklistCategory(categoryId) {
  return apiDelete(`/profile/categories/blacklisted/${categoryId}`)
}


export function getRecommendedCategories(jwt) {
  return apiGet('/recommendations/categories/', null, jwt)
}
export function incRecommendedCategoriesDisplay(categoryId) {
  return apiPost(`/recommendations/categories/${categoryId}/display`, null)
}
export function setRecommendedCategoriesAccept(categoryId) {
  return apiPost(`/recommendations/categories/${categoryId}/accept`, null)
}
export function setRecommendedCategoriesReject(categoryId) {
  return apiPost(`/recommendations/categories/${categoryId}/reject`, null)
}


export function getWeather(params) {
  return apiGet('/weather/', params)
}
export function searchWeatherLocations(searchString) {
  return apiGet('/weather/locations', {query: searchString})
}

export function addWeatherLocation(locationId) {
  return apiPost(`/profile/weather/locations/${locationId}`, null)
}
export function removeWeatherLocation(locationId) {
  return apiDelete(`/profile/weather/locations/${locationId}`)
}
export function setDefaultWeatherLocation(locationId) {
  return apiPut(`/profile/weather/default-location/${locationId}`, null)
}

export function loginOrRegisterWithAlmaTunnus(accessToken, tempProfile) {
  const postData = {
    id_token: accessToken,
  }

  // If this a new user, and the user has a temporary profile saved in cookies,
  // we pass the temporary profile data to the API so it can be used to create
  // the new user profile.
  if (tempProfile) {
    postData.whitelisted_categories = tempProfile.whitelistedCats.join(',')
    postData.whitelisted_tags = tempProfile.whitelistedTags.join(',')
    postData.blacklisted_categories = tempProfile.blacklistedCats.join(',')
    postData.blacklisted_tags = tempProfile.blacklistedTags.join(',')
  }
  return apiPost('/profile/almalogin', postData)
}

export function logoutAmpparitUser() {
  return apiPost('/profile/logout')
}

export function getUser(jwt) {
  return apiGet('/profile/', null, jwt)
}
